import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, LayoutDashboard, MessageSquareWarning, CreditCard } from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';

const EnPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CorporateNavbar />

      {/* Hero */}
      <section
        style={{
          background: 'linear-gradient(160deg, #0a0118 0%, #1e0a3c 40%, #0d1b4b 70%, #0a0118 100%)',
          position: 'relative',
          overflow: 'hidden',
          paddingTop: 140,
          paddingBottom: 100,
        }}
      >
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(99,102,241,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.07) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div aria-hidden="true" style={{ position: 'absolute', top: -100, right: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 65%)', zIndex: 0, pointerEvents: 'none' }} />

        <div className="container mx-auto px-6 text-center" style={{ position: 'relative', zIndex: 10 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.35)', color: '#a5b4fc', borderRadius: 100, padding: '6px 16px', fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#818cf8', display: 'inline-block' }} />
              AI automation · SaaS · Sweden
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.65 }}
            style={{ color: '#fff', fontWeight: 800, fontSize: 'clamp(36px, 5.5vw, 68px)', lineHeight: 1.08, letterSpacing: '-0.025em', marginBottom: 24 }}
          >
            We build AI systems that<br />
            <span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              remove manual work
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.65 }}
            style={{ color: '#94a3b8', fontSize: 18, lineHeight: 1.7, maxWidth: 560, margin: '0 auto 40px' }}
          >
            We build systems that do the work for you. Automation, AI and SaaS for B2B companies — delivered in 2–4 weeks.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.65 }}
            style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}
          >
            <Link to="/en/contact">
              <button style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', border: 'none', color: 'white', padding: '14px 28px', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: '0 0 24px rgba(99,102,241,0.5)', transition: 'all 0.25s ease' }}>
                Describe your needs
                <ArrowRight size={16} />
              </button>
            </Link>
            <Link to="/en/klagobot">
              <button style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '14px 28px', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer', transition: 'all 0.25s ease' }}>
                See our products
              </button>
            </Link>
          </motion.div>
        </div>

        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, lineHeight: 0, zIndex: 11 }}>
          <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%' }}>
            <path d="M0,60 C360,0 1080,0 1440,60 L1440,60 L0,60 Z" fill="#F8FAFC" />
          </svg>
        </div>
      </section>

      {/* What we do */}
      <section className="py-24 bg-surface-subtle">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">What we build</span>
            <h2 className="text-3xl sm:text-[42px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
              How we can help
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { icon: Zap, title: 'AI Automations', desc: 'Automate repetitive tasks — lead handling, invoices, bookings, customer service. Your team focuses on what actually matters.', href: '/automations', cta: 'See automations', gradient: 'from-amber-400 to-orange-500' },
              { icon: LayoutDashboard, title: 'SaaS Applications', desc: 'Custom-built tools that solve specific business problems. From idea to working product in weeks.', href: '/ai-consulting', cta: 'See services', gradient: 'from-indigo-500 to-violet-600' },
              { icon: MessageSquareWarning, title: 'Ready-made products', desc: 'Klagobot and Swishkassa are our own products — ready to use without a custom project.', href: '/en/klagobot', cta: 'See products', gradient: 'from-violet-500 to-purple-600' },
            ].map(({ icon: Icon, title, desc, href, cta, gradient }) => (
              <motion.div
                key={title}
                whileHover={{ y: -5 }}
                className="group bg-white rounded-2xl border border-border-light p-7 flex flex-col shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} items-center justify-center mb-5 shadow-md`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-[18px] font-bold text-text-primary mb-3">{title}</h3>
                <p className="text-[14px] text-text-secondary leading-relaxed flex-1 mb-5">{desc}</p>
                <Link to={href} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-indigo">
                  {cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products spotlight */}
      <section className="py-20 bg-white border-t border-border-light">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-surface-subtle rounded-2xl border border-border-light p-8">
              <div className="inline-flex w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 items-center justify-center mb-4 shadow-md">
                <MessageSquareWarning className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-text-primary mb-3">Klagobot</h3>
              <p className="text-text-secondary leading-relaxed mb-5">
                Generate complaint letters and appeals in minutes. Describe your case — Klagobot writes a professional, ready-to-send letter.
              </p>
              <Link to="/en/klagobot" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-indigo">
                Learn more <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-surface-subtle rounded-2xl border border-border-light p-8">
              <div className="inline-flex w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 items-center justify-center mb-4 shadow-md">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-text-primary mb-3">Swishkassa <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full ml-1">Coming soon</span></h3>
              <p className="text-text-secondary leading-relaxed mb-5">
                Simple payment system for associations and events. Multiple sales points, Swish QR per register, automatic summaries.
              </p>
              <Link to="/en/swishkassa" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-600">
                Register interest <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-extrabold text-text-primary mb-4">Ready to get started?</h2>
          <p className="text-text-secondary mb-8 max-w-md mx-auto">Tell us what takes unnecessary time. We'll get back with a concrete proposal — no pitch, just a solution.</p>
          <Link to="/en/contact">
            <button className="group btn-glow relative overflow-hidden bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2">
              Send a request
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
          </Link>
          <p className="text-xs text-text-muted mt-3">Response within 24 hours · You get a concrete proposal</p>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default EnPage;
